/**
 * ApprovalInbox — every expense and transfer waiting on a second approver.
 *
 * Cards rather than a table, because approvals happen on a phone between
 * meetings. Your own requests are listed but not approvable: the initiator can
 * never be the approver.
 */
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, ArrowLeftRight, BellRing, CheckCircle2, Clock, Loader2, Receipt, XCircle } from 'lucide-react';
import { authHeaders } from '@/lib/authToken';
import { AlertRecipientsDialog } from '@/components/bank/AlertRecipientsDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const call = async (path, opts = {}) => {
  const r = await fetch(`${API_URL}${path}`, {
    ...opts,
    headers: { ...authHeaders(), 'Content-Type': 'application/json', ...opts.headers },
  });
  const body = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(body.detail || 'Request failed');
  return body;
};

const money = (n, code) => `${code || ''} ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`.trim();

const waited = (hours) => {
  if (hours == null) return 'just now';
  if (hours < 1) return 'a few minutes';
  if (hours < 24) return `${Math.round(hours)}h`;
  return `${Math.round(hours / 24)}d`;
};

export default function ApprovalInbox() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState('');
  const [alertsOpen, setAlertsOpen] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['approval-inbox'],
    queryFn: () => call('/api/bank/transfers/pending'),
    refetchInterval: 30000,
    retry: false,
  });

  const rows = data?.transfers || [];
  const mine = rows.filter((r) => r.is_mine).length;

  const { data: scope } = useQuery({
    queryKey: ['my-finance-scope'],
    queryFn: () => call('/api/bank/finance-scope'),
    retry: false,
  });

  const approve = useMutation({
    mutationFn: (id) => call(`/api/bank/transfers/${id}/approve`, {
      method: 'POST', body: JSON.stringify({ notes: 'Approved from inbox' }),
    }),
    onSuccess: () => {
      toast.success('Approved — the money has moved');
      qc.invalidateQueries({ queryKey: ['approval-inbox'] });
      qc.invalidateQueries({ queryKey: ['bank-dash'] });
    },
    onError: (e) => toast.error(e.message),
  });

  const reject = useMutation({
    mutationFn: ({ id, why }) => call(`/api/bank/transfers/${id}/reject`, {
      method: 'POST', body: JSON.stringify({ reason: why }),
    }),
    onSuccess: () => {
      toast.success('Rejected — nothing moved');
      setRejecting(null);
      setReason('');
      qc.invalidateQueries({ queryKey: ['approval-inbox'] });
    },
    onError: (e) => toast.error(e.message),
  });

  return (
    <div className="min-h-screen bg-slate-50" data-testid="approval-inbox">
      <header className="border-b bg-white sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-3 sm:px-4 py-3 flex items-center gap-2 sm:gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin')} data-testid="approvals-back">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-lg font-bold flex items-center gap-2">
              <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-amber-600 shrink-0" /> Approvals
            </h1>
            <p className="text-[11px] sm:text-xs text-slate-500">
              {rows.length} waiting{mine ? ` · ${mine} raised by you` : ''}
            </p>
            {scope && (
              <p className="text-[11px] text-slate-400 truncate" data-testid="approvals-scope">
                {scope.platform_wide
                  ? 'You approve platform-wide'
                  : `You approve for ${scope.label}`}
              </p>
            )}
          </div>
          {rows.length > 0 && (
            <Badge className="bg-amber-100 text-amber-800" data-testid="approvals-count">{rows.length}</Badge>
          )}
          <Button variant="ghost" size="icon" data-testid="alert-settings-btn"
                  onClick={() => setAlertsOpen(true)}>
            <BellRing className="h-5 w-5 text-slate-500" />
          </Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-3 sm:p-4 space-y-3">
        {isLoading && (
          <div className="py-16 text-center text-slate-400" data-testid="approvals-loading">
            <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" /> Checking the queue…
          </div>
        )}
        {error && (
          <Card><CardContent className="p-6 text-sm text-rose-600" data-testid="approvals-error">{error.message}</CardContent></Card>
        )}
        {!isLoading && !error && rows.length === 0 && (
          <Card>
            <CardContent className="p-10 text-center" data-testid="approvals-empty">
              <CheckCircle2 className="h-8 w-8 text-emerald-500 mx-auto mb-3" />
              <p className="text-sm text-slate-600">Nothing waiting. Every request has been dealt with.</p>
            </CardContent>
          </Card>
        )}

        {rows.map((r) => {
          const isExpense = r.kind === 'expense';
          const Icon = isExpense ? Receipt : ArrowLeftRight;
          return (
            <Card key={r.id} className="border-slate-200" data-testid={`approval-${r.id}`}>
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-start gap-3">
                  <div className="h-9 w-9 shrink-0 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center">
                    <Icon className="h-4 w-4 text-amber-700" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                      <span className="text-lg font-semibold text-slate-900" data-testid={`approval-amount-${r.id}`}>
                        {money(r.amount, r.currency)}
                      </span>
                      <Badge variant="outline" className="text-[10px] capitalize">{isExpense ? 'Expense' : 'Transfer'}</Badge>
                      {r.expense_category && (
                        <Badge variant="outline" className="text-[10px] capitalize">{String(r.expense_category).replace(/_/g, ' ')}</Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-600 mt-1 break-words">{r.memo || 'No description given'}</p>
                    <p className="text-[11px] text-slate-400 mt-1 break-words">
                      {isExpense
                        ? `From ${r.from_account_name || 'operating wallet'}`
                        : `${r.from_account_name || 'account'} → ${r.to_account_name || 'account'}`}
                    </p>
                    <p className="text-[11px] text-slate-400 mt-0.5 break-words">
                      Raised by {r.initiated_by_email || 'unknown'} · waiting {waited(r.age_hours)}
                    </p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-2 mt-3">
                  {r.can_approve ? (
                    <>
                      <Button
                        className="flex-1 h-10"
                        disabled={approve.isPending}
                        data-testid={`approve-${r.id}`}
                        onClick={() => approve.mutate(r.id)}
                      >
                        <CheckCircle2 className="h-4 w-4 mr-1.5" /> Approve
                      </Button>
                      <Button
                        variant="outline"
                        className="flex-1 h-10 text-rose-600 border-rose-200 hover:bg-rose-50"
                        data-testid={`reject-${r.id}`}
                        onClick={() => { setRejecting(r); setReason(''); }}
                      >
                        <XCircle className="h-4 w-4 mr-1.5" /> Reject
                      </Button>
                    </>
                  ) : (
                    <p className="text-[11px] text-slate-400 flex-1" data-testid={`approval-own-${r.id}`}>
                      You raised this — someone else has to approve it
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </main>

      <AlertRecipientsDialog open={alertsOpen} onClose={() => setAlertsOpen(false)} />

      <Dialog open={!!rejecting} onOpenChange={() => setRejecting(null)}>
        <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reject this request?</DialogTitle>
            <DialogDescription>
              Nothing moves. The person who raised it will see your reason.
            </DialogDescription>
          </DialogHeader>
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why are you rejecting it?"
            data-testid="reject-reason"
          />
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" className="w-full sm:w-auto" onClick={() => setRejecting(null)}>Cancel</Button>
            <Button
              className="w-full sm:w-auto bg-rose-600 hover:bg-rose-700"
              disabled={!reason.trim() || reject.isPending}
              data-testid="reject-confirm"
              onClick={() => reject.mutate({ id: rejecting.id, why: reason.trim() })}
            >
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
